import { DataTablePrimeReact, TableERP } from "@finanedi.es/storybook"
import { useQuery } from "@tanstack/react-query"
import { GetFacturasAceptablesDtoProps } from "api/DTOs/GetFacturasAceptablesDto"
import { GetPagaresAceptablesDtoProps } from "api/DTOs/GetPagaresAceptablesDto"
import { putVerificarFactura } from "api/Factura.service"
import { putVerificarPagare } from "api/Pagare.service"
import { EstadoVerificacionEfectoType } from "api/Types/EstadoVerificacionEfecto"
import { useValidacionesPendientesUsuario } from "api/Usuario.hook"
import NativePDF from "common/components/NativePDF/NativePDF"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import { Skeleton } from "primereact/skeleton"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import {
  downloadDocumentoFetch,
  EstadoFinanacionDetailResponseProps,
  FINANCIACION_TYPE,
} from "../../constant"
import { FINANCIACION_DETAIL_COLUMNS } from "../../constants/FinanciacionDetailColumns"
import FinanciacionVerification from "./components/FinanciacionVerification"

export const FinanciacionDetail = () => {
  const { t } = useTranslation()
  const params = useParams()
  const {
    location: { state },
  } = useHistory()
  const history = useHistory()

  const { usuarioValidacionesPendienteRefetch } =
    useValidacionesPendientesUsuario()

  const [validacionesSelected, setValidacionesSelected] = useState<
    GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
  >(
    state.validacionesSelected[0].length > 0
      ? state.validacionesSelected[0]
      : state.validacionesSelected ?? []
  )

  const [rowSelected, setRowSelected] = useState(params.id ?? undefined)
  const [dataToValidate, setDataTovalidate] =
    useState<EstadoFinanacionDetailResponseProps>({
      estadoVerificacion: null,
      observaciones: "",
    })

  const dtRef =
    useRef<
      DataTablePrimeReact<
        GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
      >
    >(null)

  const { data: pdfDocument, isLoading: pdfDocumentIsLoading } = useQuery({
    queryKey: ["pdf", rowSelected, state.selectedTab],
    enabled: Boolean(rowSelected),
    queryFn: () => {
      if (!rowSelected) return
      return downloadDocumentoFetch(state.selectedTab, rowSelected)
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      return (
        failureCount < 10 && error.message !== "Tipo de documento no soportado"
      )
    },
    retryDelay: 2000,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const getIdsFacturas = (
    array: GetFacturasAceptablesDtoProps[] | GetPagaresAceptablesDtoProps[]
  ): string[] | null => {
    if (!array || array.length === 0) return null

    const ids = array
      .map((elemento) => elemento.id)
      .filter((id): id is string => typeof id === "string")

    return ids.length > 0 ? ids : null
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!dataToValidate.estadoVerificacion) return
    const body = {
      estado: dataToValidate.estadoVerificacion,
      facturaIds: getIdsFacturas(validacionesSelected),
      observaciones: dataToValidate.observaciones,
    }
    try {
      if (state.selectedTab === FINANCIACION_TYPE.FACTURAS) {
        await putVerificarFactura(body)
      } else {
        await putVerificarPagare(body)
      }

      usuarioValidacionesPendienteRefetch()
      history.push({ pathname: ROUTES.VALIDACIONES_FINANCIACION, state })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Ocurrió un error al verificar la ${params.type}:`, error)
    }
  }

  const handleSelect = ({ value }: { value: EstadoVerificacionEfectoType }) => {
    setDataTovalidate({ ...dataToValidate, estadoVerificacion: value })
  }

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDataTovalidate({
      ...dataToValidate,
      observaciones: value,
    })
  }

  return (
    <div className='tw-overflow-hidden tw-h-full tw-flex tw-flex-row'>
      <div className='tw-w-4/6 tw-h-full'>
        <div className='tw-h-1/2'>
          {pdfDocumentIsLoading ? (
            <Skeleton className='tw-w-full !tw-h-full' />
          ) : (
            <NativePDF
              pdf={pdfDocument}
              type='stream'
            />
          )}
        </div>
        <div className='tw-h-1/2'>
          <TableERP
            refTable={dtRef}
            columns={FINANCIACION_DETAIL_COLUMNS(t, setRowSelected)}
            className='tw-h-full'
            columnResizeMode='expand'
            removableSort
            value={
              state.validacionesSelected[0].length > 0
                ? state.validacionesSelected[0]
                : state.validacionesSelected ?? []
            }
            selection={validacionesSelected}
            selectionMode='checkbox'
            size='small'
            selectionColumnMode='multiple'
            onSelectionChange={({
              value,
            }: {
              originalEvent: React.SyntheticEvent
              value:
                | GetFacturasAceptablesDtoProps[]
                | GetPagaresAceptablesDtoProps[]
              type?: "multiple" | "all" | "checkbox" | "row" | undefined
            }) => {
              setValidacionesSelected(value)
            }}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-venta.svg`}
                title='nsProformaInvoices.header.title'
                description='shared.noDataDescription'
                actionLabel='nsProformaInvoices.header.newInvoice'
                showActions={false}
              />
            }
          />
        </div>
      </div>
      <div className='tw-w-2/6 tw-h-full'>
        <FinanciacionVerification
          documentsToValidate={validacionesSelected}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          dataToValidate={dataToValidate}
          handleSelect={handleSelect}
          type={state.selectedTab}
        />
      </div>
    </div>
  )
}
